import { useState, useEffect, useCallback, useRef } from "react";
import { fetchWhatsAppAccountStatus, retryWhatsAppOnboarding } from "../api/whatsappApi";

const POLL_INTERVAL_MS = 3000;

// Statuses where we keep polling — processing is in flight
const POLLING_STATUSES = new Set(["connecting"]);

export function useWhatsAppAccountStatus(tenantId) {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const isFirstFetch = useRef(true);
  const pollCountRef = useRef(0);
  const prevStageRef = useRef(null);
  const prevStatusRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      // console.log(`⏹️ [WhatsApp Pipeline Polling] Stopped polling for tenant: ${tenantId}`);
    }
  }, [tenantId]);

  const fetchOnce = useCallback(async ({ showLoading = false } = {}) => {
    if (!tenantId) return;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const response = await fetchWhatsAppAccountStatus(tenantId);
      const statusData = response?.data?.data ?? response?.data ?? response ?? null;
      setAccountStatus(statusData);

      pollCountRef.current += 1;
      const count = pollCountRef.current;
      const currStatus = statusData?.status;
      const currStage = statusData?.stage;
      const prevStatus = prevStatusRef.current;
      const prevStage = prevStageRef.current;

      const stageChanged = currStatus !== prevStatus || currStage !== prevStage;

      if (stageChanged) {
        // console.log("----------------------------------------------------------------");
        console.log(`⏳ [DocLift WhatsApp Pipeline] STAGE TRANSITION DETECTED (Poll #${count}):`, {
          tenantId,
          previous: { status: prevStatus, stage: prevStage },
          current: { status: currStatus, stage: currStage },
          phoneNumber: statusData?.phone_number || statusData?.display_phone_number,
          message: statusData?.message,
          availableActions: statusData?.available_actions,
          timestamp: new Date().toISOString(),
        });
        // console.log("----------------------------------------------------------------");
        prevStatusRef.current = currStatus;
        prevStageRef.current = currStage;
      } else {
        // console.log(`📡 [DocLift WhatsApp Pipeline Poll #${count}] Status: "${currStatus}" | Stage: "${currStage || "N/A"}"`);
      }

      // Stop polling when processing has reached a terminal state
      if (statusData && !POLLING_STATUSES.has(statusData.status)) {
        stopPolling();
        if (statusData.status === "connected") {
          // console.log("================================================================");
          // console.log("🎉 [DocLift WhatsApp Pipeline] FINAL STAGE: CONNECTED SUCCESSFULLY!");
          // console.log("Tenant:", tenantId);
          // console.log("Status: connected");
          // console.log("WhatsApp Phone:", statusData?.phone_number || statusData?.display_phone_number);
          // console.log("Account ID:", statusData?.account_id || statusData?.waba_id);
          // console.log("================================================================");
        } else {
          console.warn("⚠️ [DocLift WhatsApp Pipeline] Stopped polling on non-connecting status:", {
            status: statusData.status,
            stage: statusData.stage,
            message: statusData.message,
          });
        }
      }
    } catch (err) {
      console.error("❌ [DocLift WhatsApp Pipeline] Failed to fetch account status:", {
        status: err?.status,
        message: err?.message,
      });
      setError(err?.message || "Failed to fetch WhatsApp account status.");
      stopPolling(); // Stop polling on error to avoid hammering
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [tenantId, stopPolling]);

  // Start polling loop when status enters "connecting"
  const startPolling = useCallback(() => {
    if (pollRef.current) return; // Already polling
    pollCountRef.current = 0;
    // console.log(`🔄 [WhatsApp Pipeline Polling] Started polling interval (${POLL_INTERVAL_MS}ms) for tenant: ${tenantId}`);
    pollRef.current = setInterval(() => {
      fetchOnce({ showLoading: false });
    }, POLL_INTERVAL_MS);
  }, [tenantId, fetchOnce]);

  // Manual refetch — resets loading state and re-evaluates polling
  const refetch = useCallback(async () => {
    stopPolling();
    isFirstFetch.current = true;
    setLoading(true);
    // Artificial delay so the user can clearly see the loading state
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await fetchOnce({ showLoading: true });
  }, [fetchOnce, stopPolling]);

  // Initial fetch on mount / tenantId change
  useEffect(() => {
    if (!tenantId) return;
    isFirstFetch.current = true;
    stopPolling();
    fetchOnce({ showLoading: true });

    return () => {
      stopPolling();
    };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to status changes: start polling if status is "connecting"
  useEffect(() => {
    if (!accountStatus) return;
    if (POLLING_STATUSES.has(accountStatus.status)) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [accountStatus?.status, startPolling, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const executeRetry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await retryWhatsAppOnboarding(tenantId);
      const statusData = response?.data?.data ?? null;
      if (statusData) {
        setAccountStatus(statusData);
      } else {
        setAccountStatus((prev) => ({
          ...prev,
          status: "connecting",
          message: "Resuming WhatsApp processing pipeline.",
        }));
      }
      startPolling();
    } catch (err) {
      // console.error("[useWhatsAppAccountStatus] retry failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, startPolling]);

  return { accountStatus, loading, error, refetch, executeRetry };
}
